import { copy } from "../../config/copy.js";
import { VOICES, voiceById } from "../../config/voices.js";
import { h } from "../../core/dom.js";
import { resetEverything, resetSummary } from "../../storage/reset.js";
import { shareText } from "../share.js";

function voicePicker(app) {
  const select = h("select", { class: "field", "aria-label": copy.voiceTitle },
    VOICES.map((voice) => h("option", { value: voice.id }, `${voice.label} · ${voice.note}`)));
  select.value = app.speaker.voice;
  const preview = h("button", { class: "btn btn-ghost", type: "button" }, "▶️ Prova la voce");
  select.addEventListener("change", () => {
    app.speaker.setVoice(select.value);
    app.toast(copy.voiceSaved(voiceById(select.value).label), "success");
  });
  preview.addEventListener("click", async () => {
    if (app.speaker.speaking) return app.speaker.stop();
    preview.disabled = true;
    try {
      const spoken = await app.speaker.speak(copy.voiceSample, { voice: select.value });
      if (!spoken) app.toast(copy.voiceError, "warning");
    } finally {
      preview.disabled = false;
    }
  });
  return [h("p", { class: "muted" }, copy.voiceHint), select, preview];
}

function coachToggle(app) {
  const label = () => (app.coachEnabled.enabled ? "🎙️ Guida vocale attiva" : "🔇 Guida vocale disattivata");
  const button = h("button", { class: "btn btn-ghost wide", type: "button" }, label());
  button.addEventListener("click", () => {
    const on = app.coachEnabled.toggle();
    button.textContent = label();
    app.toast(on ? copy.coachOn : copy.coachOff, "info", 3000);
  });
  const liveLabel = () => (app.coachLive.enabled ? "🎥 Parla anche in Live" : "🎥 In Live resta zitta");
  const liveButton = h("button", { class: "btn btn-ghost wide", type: "button" }, liveLabel());
  liveButton.addEventListener("click", () => {
    const on = app.coachLive.toggle();
    liveButton.textContent = liveLabel();
    app.toast(on ? copy.coachLiveOn : copy.coachLiveOff, "info", 3000);
  });
  return [h("h3", {}, copy.coachTitle), h("p", { class: "muted" }, copy.coachHint), button,
    h("p", { class: "fineprint" }, copy.coachLiveHint), liveButton];
}

export function voiceSection(app) {
  const parts = [
    ...(app.speaker?.remote ? voicePicker(app) : []),
    ...(app.coach?.configured ? coachToggle(app) : [])
  ];
  if (!parts.length) return null;
  return h("div", { class: "voice-picker" }, parts);
}

export function settingsSection(app, onReset) {
  const shareButton = h("button", { class: "btn btn-secondary wide", type: "button" }, "🔗 Condividi PAWS");
  shareButton.addEventListener("click", async () => {
    const link = location.origin + location.pathname;
    try {
      const outcome = await shareText(`${copy.appName} · ${copy.tagline}\n${link}`, app.toast);
      if (outcome === "non disponibile") app.toast("Copia questo indirizzo: " + link, "info", 6000);
    } catch (error) {
      app.reporter.report(error, "share-app");
      app.toast(copy.shareError, "error");
    }
  });

  const resetRow = h("div", { class: "reset-row" });
  const askReset = h("button", { class: "btn btn-ghost wide", type: "button" }, "🧹 Azzera tutto");
  const runReset = async () => {
    resetRow.replaceChildren(h("p", { class: "muted" }, "Sto azzerando…"));
    try {
      const report = await resetEverything(app);
      app.toast(resetSummary(report), report.errori.length ? "warning" : "success", 6000);
      onReset?.();
    } catch (error) {
      app.reporter.report(error, "reset");
      app.toast("Non sono riuscito ad azzerare tutto.", "error");
    } finally {
      resetRow.replaceChildren(askReset);
    }
  };
  askReset.addEventListener("click", () => {
    resetRow.replaceChildren(
      h("p", { class: "muted" }, "Cancello lo storico di questo dispositivo, i feedback che hai inviato e le tue preferenze. Non si può annullare."),
      h("div", { class: "detail-actions" },
        h("button", { class: "btn btn-ghost", type: "button", onclick: () => resetRow.replaceChildren(askReset) }, "Annulla"),
        h("button", { class: "btn btn-danger", type: "button", onclick: runReset }, "Sì, azzera tutto")));
  });
  resetRow.append(askReset);

  return h("div", { class: "settings" },
    shareButton,
    h("p", { class: "fineprint" }, "Condivide solo l'indirizzo dell'app: nessuna tua foto o analisi."),
    resetRow);
}
